/**
 * 加载脚本
 * @param url 脚本地址
 * @param id 脚本id
 */
export const loadScript = (url: string, id: string) => {
  return new Promise<void>((resolve, reject) => {
    if (document.getElementById(id)) return resolve();
    const script = document.createElement('script');
    script.src = url;
    script.id = id;
    script.onload = () => {
      resolve();
    };
    script.onerror = err => {
      reject(err);
    };
    document.body.appendChild(script);
  });
};

/**
 * 通用的脚本加载函数，可以加载任何外部脚本
 * @param src 脚本地址
 * @param id 脚本id
 * @returns
 */
export const loadExternalScript = (src, id) => {
  return new Promise((resolve, reject) => {
    if (document.getElementById(id)) {
      return resolve(true);
    }

    const script = document.createElement('script');
    script.id = id;
    script.src = src;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(true);
    script.onerror = () => reject(new Error(`Script loading failed: ${src}`));

    const firstScriptTag = document.getElementsByTagName('script')[0];
    firstScriptTag.parentNode.insertBefore(script, firstScriptTag);
  });
};

/**
 * 在浏览器空闲时加载脚本，同时设置最大等待时间兜底。
 *
 * 优先级策略：
 * - 使用 requestIdleCallback 让脚本在浏览器空闲时加载，不影响 LCP/FCP 等 Web Vitals
 * - 设置 maxDelay 超时兜底（默认 5s），即使浏览器一直忙碌也保证脚本最终加载
 * - 不依赖 window.load 事件，不会被挂起的图片等资源阻塞
 */
export const loadScriptWhenIdle = (
  src: string,
  scriptId: string,
  {
    onLoad,
    onError,
    maxDelay = 5000,
    retryCount = 1
  }: {
    onLoad?: () => void;
    onError?: (error: Event) => void;
    maxDelay?: number;
    retryCount?: number;
  } = {}
) => {
  let loaded = false;

  const inject = () => {
    if (loaded || document.getElementById(scriptId)) return;
    loaded = true;

    const script = document.createElement('script');
    script.src = src;
    script.id = scriptId;
    script.async = true;

    script.addEventListener('load', () => onLoad?.());
    script.addEventListener('error', e => {
      script.remove();
      if (retryCount > 0) {
        loadScriptWhenIdle(src, scriptId, {
          onLoad,
          onError,
          maxDelay: 1000,
          retryCount: retryCount - 1
        });
      } else {
        onError?.(e);
      }
    });

    document.body.appendChild(script);
  };

  if (typeof requestIdleCallback === 'function') {
    requestIdleCallback(inject, { timeout: maxDelay });
  } else {
    setTimeout(inject, 0);
  }
};

/**
 * 通用的脚本加载函数，可以加载任何外部脚本，并增加了重试机制
 * @param src 脚本地址
 * @param id 脚本id
 * @param options 选项
 * @returns
 */
export const loadExternalScriptWithRetry = (
  src,
  id,
  options?: { retries?: number; timeout?: number; retryDelay?: number }
) => {
  // 从选项中解构参数，并设置默认值
  const { retries = 1, timeout = 5000, retryDelay = 500 } = options;

  // 递归的尝试函数
  const attempt = attemptsLeft => {
    return new Promise((resolve, reject) => {
      // 检查脚本是否已存在（可能在上次尝试失败后，又有其他代码加载了它）
      const existingScriptOnRetry = document.getElementById(id);
      if (existingScriptOnRetry) {
        return resolve(true);
      }

      const script = document.createElement('script');
      script.id = id;
      script.src = src;
      script.async = true;

      let timer;

      // 清理函数，负责移除事件监听、定时器和失败的脚本标签
      const cleanupAndReject = error => {
        clearTimeout(timer);
        script.onload = null;
        script.onerror = null;
        // 从DOM中移除失败的脚本标签，为下一次重试做准备
        if (script.parentNode) {
          script.parentNode.removeChild(script);
        }
        reject(error);
      };

      // 设置加载超时定时器
      timer = setTimeout(() => {
        cleanupAndReject(new Error(`timeout after ${timeout}ms`));
      }, timeout);

      script.onload = () => {
        clearTimeout(timer);
        script.onload = null;
        script.onerror = null;
        resolve(true);
      };

      script.onerror = () => {
        cleanupAndReject(new Error('script_error'));
      };

      document.head.appendChild(script);
    }).catch(error => {
      console.error(`Failed to load ${src}: ${error.message}. Retries left: ${attemptsLeft}`);
      // 如果还有重试次数
      if (attemptsLeft > 0) {
        // 等待一小段时间再重试
        return new Promise(resolve => setTimeout(resolve, retryDelay)).then(() => attempt(attemptsLeft - 1)); // 递归调用，次数减一
      }
      // 所有重试都失败了，抛出最终的错误
      throw new Error(`Failed to load script ${src} after ${retries + 1} attempts.`);
    });
  };

  // 首次调用前，先检查脚本是否已存在
  if (document.getElementById(id)) {
    return Promise.resolve(true);
  }

  // 开始第一次尝试，传入配置的重试次数
  return attempt(retries);
};
